//@ts-nocheck
import { orgSelectors } from 'ducks/orgs'
import { walletSettingsSelectors } from 'ducks/wallet'
import { Box, Paper, styled } from 'opensolar-ui'
import React, { lazy, Suspense, useMemo } from 'react'
import { Form } from 'react-final-form'
import { useSelector } from 'react-redux'
import { getTopUpAmtChoices, getTopUpTriggerChoices, walletCurrencyFromCountryIso2 } from './constants'
import { walletManageCardPaperSx, WALLET_PAGE_SECTION_GAP_PX } from './walletCardFieldShellStyles'

export type { StripeInfo, StripeInfoAndRefType, WalletRequestBodyType } from './walletStripeTypes'

export const WalletPageRoot = styled(Box, { name: 'WalletPageRoot' })(({ theme }) => ({
  background: theme.palette.grey[100],
  minHeight: '100%',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'hidden',
  padding: '23px 24px 90px',
  boxSizing: 'border-box',
}))

export const WalletActivityOuter = styled(Box, { name: 'WalletActivityOuter' })({
  marginTop: 8,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  /* main.css applies .list-page { padding: 24px 24px 90px }; wallet page already has WalletPageRoot padding */
  '& .list-page': {
    padding: 0,
  },
})

/** Shared column for balance + manage-wallet content so both cards share the same width (flex-safe). */
export const WalletMainColumn = styled(Box, { name: 'WalletMainColumn' })({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
  maxWidth: '75%',
  minWidth: 0,
  boxSizing: 'border-box',
})

export const WalletContentBox = styled(Box, { name: 'WalletContentBox' })({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
})

export const WalletToolbarRow = styled('div', { name: 'WalletToolbarRow' })({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
})

export const WalletToolbarButtonGap = styled('div', { name: 'WalletToolbarButtonGap' })({
  marginRight: 20,
})

export const LoadingSkeletonGap = styled('div', { name: 'LoadingSkeletonGap' })({
  height: 20,
})

export const BalancePaper = styled(Paper, { name: 'BalancePaper' })(({ theme }) => ({
  ...walletManageCardPaperSx,
  marginBottom: WALLET_PAGE_SECTION_GAP_PX,
}))

const WalletHomeContents = lazy(() => import('./WalletHomeContents'))

const WalletFormInner: React.FC = () => {
  const wallet = useSelector(walletSettingsSelectors.getWallet)
  const isActivated = useSelector(walletSettingsSelectors.getIsWalletActivated)
  const orgCountry = useSelector(orgSelectors.getOrgIso2)

  const initialValues = useMemo(() => {
    if (isActivated) {
      return {
        tax_identifier: wallet?.tax_identifier ?? '',
        enable_auto_top_up: wallet?.enable_auto_top_up ?? false,
        top_up_amount: wallet?.top_up_amount ?? 0,
        top_up_trigger_amount: wallet?.top_up_trigger_amount ?? 0,
        funds_to_add: undefined,
      }
    }
    const billingCurrency = wallet?.currency || walletCurrencyFromCountryIso2(orgCountry)
    const amtChoices = getTopUpAmtChoices(billingCurrency)
    const trigChoices = getTopUpTriggerChoices(billingCurrency)
    const defaultAmt = amtChoices[0]?.id ?? 100
    const defaultTrig = trigChoices[0]?.id ?? 100
    const topAmt =
      wallet?.top_up_amount && wallet.top_up_amount > 0 ? wallet.top_up_amount : defaultAmt
    const topTrig =
      wallet?.top_up_trigger_amount && wallet.top_up_trigger_amount > 0
        ? wallet.top_up_trigger_amount
        : defaultTrig
    return {
      tax_identifier: wallet?.tax_identifier ?? '',
      enable_auto_top_up: true,
      top_up_amount: topAmt,
      top_up_trigger_amount: topTrig,
      funds_to_add: undefined,
    }
  }, [
    isActivated,
    wallet?.id,
    wallet?.currency,
    orgCountry,
    wallet?.tax_identifier,
    wallet?.enable_auto_top_up,
    wallet?.top_up_amount,
    wallet?.top_up_trigger_amount,
  ])

  const formKey = isActivated && wallet?.id ? `wallet-${wallet.id}` : 'wallet-new'

  return (
    <Form
      key={formKey}
      initialValues={initialValues}
      onSubmit={(e) => e.preventDefault()}
      render={(props) => (
        <Suspense fallback={null}>
          <WalletHomeContents {...props} />
        </Suspense>
      )}
    />
  )
}

const WalletHomeWrapper: React.FC = () => <WalletFormInner />

export default WalletHomeWrapper
